import {
  listCargaTrabalhoObras,
  listCargaTrabalhoProfissionais,
} from "@/modules/carga-trabalho/carga-trabalho.gateway";
import { listAlocacoesPorLocal } from "@/modules/carga-de-trabalho/carga-de-trabalho.gateway";
import { listAlocacoesPorFornecedor } from "../recursos.gateway";
import { isObraCancelada } from "@/modules/carga-trabalho";
import type { CargaTrabalhoIntervalo } from "@/modules/carga-trabalho";
import { alocacoesDaAlteracao } from "./simulacao.conflitos";
import { chaveAlteracao } from "./simulacao.model";
import type { AlteracaoSimulada, CenarioSimulacao } from "./simulacao.model";
import { categoriaDaAlteracao } from "./simulacao.resumo";
import {
  AVISO_DATAS_ALTERADAS,
  AVISO_OBRA_CANCELADA,
  MS,
  TEXTO_ALOCACAO_INEXISTENTE,
} from "./simulacao.textos";

const mesmosIds = (a: readonly string[], b: readonly string[]) =>
  a.length === b.length && a.every((id) => b.includes(id));

/**
 * "Retomar" (HU 2221): depois de uma queda de conexão, a requisição pode ter sido gravada
 * sem que a resposta chegasse. Antes de reenviar, consulta a carga (só leitura) no período
 * da alteração e confere se o efeito dela já está lá.
 */
export async function verificarSeJaGravada(alteracao: AlteracaoSimulada): Promise<boolean> {
  switch (alteracao.acao) {
    case "definir-equipe": {
      const carga = await listCargaTrabalhoObras({
        periodo: alteracao.periodo,
        filters: { obraId: alteracao.obra.id },
      });
      const obra = carga.obras.find((item) => item.id === alteracao.obra.id);
      return Boolean(
        obra &&
        alteracao.novo.every((membro) =>
          obra.profissionais.some((profissional) => profissional.id === membro.usuarioId),
        ),
      );
    }
    case "remanejar-profissional": {
      const carga = await listCargaTrabalhoProfissionais({ periodo: alteracao.periodo });
      return carga.profissionais.some(
        (profissional) =>
          profissional.id === alteracao.novo.id &&
          profissional.alocacoes.some((alocacao) => alocacao.id === alteracao.alocacaoId),
      );
    }
    case "ignorar-conflito-profissional": {
      const carga = await listCargaTrabalhoProfissionais({ periodo: alteracao.conflito });
      const profissional = carga.profissionais.find(
        (item) => item.id === alteracao.profissional.id,
      );
      return Boolean(
        profissional?.conflitosIgnorados?.some((ignorado) =>
          mesmosIds(ignorado.alocacaoIds, alteracao.alocacaoIds),
        ),
      );
    }
    case "remanejar-fornecedor": {
      const carga = await listAlocacoesPorFornecedor({
        dataInicio: alteracao.periodo.inicio,
        dataFim: alteracao.periodo.fim,
      });
      return carga.some(
        (fornecedor) =>
          fornecedor.id === alteracao.novo.id &&
          fornecedor.alocacoes.some((alocacao) => alocacao.id === alteracao.alocacaoId),
      );
    }
    case "ignorar-conflito-fornecedor": {
      const carga = await listAlocacoesPorFornecedor({
        dataInicio: alteracao.conflito.inicio,
        dataFim: alteracao.conflito.fim,
      });
      return carga.some((fornecedor) =>
        fornecedor.conflitosIgnorados?.some((ignorado) =>
          mesmosIds(ignorado.alocacaoIds, alteracao.alocacaoIds),
        ),
      );
    }
    case "remanejar-local": {
      const carga = await listAlocacoesPorLocal({
        dataInicio: alteracao.periodo.inicio,
        dataFim: alteracao.periodo.fim,
      });
      return carga.locais.some(
        (local) =>
          local.id === alteracao.novo.id &&
          local.alocacoes.some((alocacao) => alocacao.id === alteracao.alocacaoId),
      );
    }
    case "ignorar-conflito-local": {
      const carga = await listAlocacoesPorLocal({
        dataInicio: alteracao.conflito.inicio,
        dataFim: alteracao.conflito.fim,
      });
      return carga.conflitosIgnorados.some((ignorado) =>
        mesmosIds(ignorado.alocacaoIds, alteracao.alocacaoIds),
      );
    }
  }
}

/* Revalidação ao clicar em Aplicar (HU 2221, RN020–RN023; comentários do Marco de 01/10). */

/**
 * Desfecho que o Aplicar dará à alteração se nada mudar até a confirmação. A "descartada" nem
 * chega a chamar o endpoint (a fila a recebe em `OpcoesFila.divergencias`).
 */
export type DesfechoDivergencia = "descartada" | "aplicada-com-aviso";

export type MotivoDivergencia =
  "profissional-removido" | "alocacao-inexistente" | "datas-alteradas" | "obra-cancelada";

/** Um potencial problema: o que mudou nos dados atuais desde que a alteração foi simulada. */
export interface DivergenciaAplicacao {
  /** Chave da alteração (`chaveAlteracao`). */
  chave: string;
  motivo: MotivoDivergencia;
  desfecho: DesfechoDivergencia;
  mensagem: string;
}

const DIVERGENCIA: Record<MotivoDivergencia, { desfecho: DesfechoDivergencia; mensagem: string }> =
  {
    // RN020: profissional removido da obra → descartada (MS010).
    "profissional-removido": { desfecho: "descartada", mensagem: MS.MS010 },
    // RN020/RN023: a alocação sumiu. Sem vínculo da alocação de fornecedor com o Escopo
    // Macro, a transferência de escopo não se prova: vale "alocação inexistente", sem a MS011.
    "alocacao-inexistente": { desfecho: "descartada", mensagem: TEXTO_ALOCACAO_INEXISTENTE },
    // RN021: datas da obra alteradas → aplicada com aviso.
    "datas-alteradas": { desfecho: "aplicada-com-aviso", mensagem: AVISO_DATAS_ALTERADAS },
    // RN022: obra cancelada → descartada.
    "obra-cancelada": { desfecho: "descartada", mensagem: AVISO_OBRA_CANCELADA },
  };

/** Alocação como chega nas três cargas: o que a revalidação compara. */
interface AlocacaoAtual {
  id: string;
  inicio: string;
  fim: string;
  obra: { situacao: string };
}

const MESMO_PERIODO = (a: CargaTrabalhoIntervalo, b: CargaTrabalhoIntervalo) =>
  a.inicio === b.inicio && a.fim === b.fim;

/** Memoriza a consulta pela chave: alterações no mesmo período compartilham a requisição. */
function consultas() {
  const cache = new Map<string, Promise<unknown>>();
  return <T>(chave: string, consultar: () => Promise<T>): Promise<T> => {
    if (!cache.has(chave)) cache.set(chave, consultar());
    return cache.get(chave) as Promise<T>;
  };
}

type Consultar = ReturnType<typeof consultas>;

/** Alocações atuais do recurso da alteração e o que ela esperava delas; `null` sem consulta. */
async function alocacoesAtuais(
  alteracao: AlteracaoSimulada,
  consultar: Consultar,
): Promise<AlocacaoAtual[] | null> {
  switch (alteracao.acao) {
    case "remanejar-profissional":
    case "ignorar-conflito-profissional": {
      const periodo = "conflito" in alteracao ? alteracao.conflito : alteracao.periodo;
      const id = "profissional" in alteracao ? alteracao.profissional.id : alteracao.anterior.id;
      const carga = await consultar(`prof:${periodo.inicio}:${periodo.fim}`, () =>
        listCargaTrabalhoProfissionais({ periodo }),
      );
      // RN019: remanejar vale mesmo que a alocação tenha trocado de dono; só some se deixou de existir.
      if (alteracao.acao === "remanejar-profissional") {
        return carga.profissionais.flatMap((item) => item.alocacoes);
      }
      return carga.profissionais.find((item) => item.id === id)?.alocacoes ?? [];
    }
    case "remanejar-fornecedor":
    case "ignorar-conflito-fornecedor": {
      const periodo = "conflito" in alteracao ? alteracao.conflito : alteracao.periodo;
      const id = "fornecedor" in alteracao ? alteracao.fornecedor.id : alteracao.anterior.id;
      const carga = await consultar(`forn:${periodo.inicio}:${periodo.fim}`, () =>
        listAlocacoesPorFornecedor({ dataInicio: periodo.inicio, dataFim: periodo.fim }),
      );
      return carga.find((item) => item.id === id)?.alocacoes ?? [];
    }
    case "remanejar-local":
    case "ignorar-conflito-local": {
      const periodo = "conflito" in alteracao ? alteracao.conflito : alteracao.periodo;
      const id = "local" in alteracao ? alteracao.local.id : alteracao.anterior.id;
      const carga = await consultar(`local:${periodo.inicio}:${periodo.fim}`, () =>
        listAlocacoesPorLocal({ dataInicio: periodo.inicio, dataFim: periodo.fim }),
      );
      return carga.locais.find((item) => item.id === id)?.alocacoes ?? [];
    }
    case "definir-equipe":
      return null;
  }
}

function motivoDaSaida(alteracao: AlteracaoSimulada): MotivoDivergencia {
  return categoriaDaAlteracao(alteracao) === "profissional"
    ? "profissional-removido"
    : "alocacao-inexistente";
}

async function motivosDaEquipe(
  alteracao: Extract<AlteracaoSimulada, { acao: "definir-equipe" }>,
  consultar: Consultar,
): Promise<MotivoDivergencia[]> {
  const carga = await consultar(`obra:${alteracao.obra.id}`, () =>
    listCargaTrabalhoObras({
      periodo: alteracao.periodo,
      filters: { obraId: alteracao.obra.id },
    }),
  );
  const obra = carga.obras.find((item) => item.id === alteracao.obra.id);
  if (!obra) return [];
  if (isObraCancelada(obra)) return ["obra-cancelada"];
  const motivos: MotivoDivergencia[] = [];
  if (
    alteracao.anterior.some(
      (membro) => !obra.profissionais.some((atual) => atual.id === membro.usuarioId),
    )
  ) {
    motivos.push("profissional-removido");
  }
  if (obra.etapas.obra && !MESMO_PERIODO(obra.etapas.obra, alteracao.periodo)) {
    motivos.push("datas-alteradas");
  }
  return motivos;
}

async function motivosDe(
  alteracao: AlteracaoSimulada,
  simulados: ReadonlySet<string>,
  consultar: Consultar,
): Promise<MotivoDivergencia[]> {
  if (alteracao.acao === "definir-equipe") return motivosDaEquipe(alteracao, consultar);

  const atuais = await alocacoesAtuais(alteracao, consultar);
  if (!atuais) return [];
  const esperadas = "alocacaoIds" in alteracao ? alteracao.alocacaoIds : [alteracao.alocacaoId];
  // Alocações criadas por uma equipe do próprio cenário ainda não existem no servidor.
  const reais = esperadas.filter((id) => !simulados.has(id));
  const encontradas = reais
    .map((id) => atuais.find((alocacao) => alocacao.id === id))
    .filter((alocacao): alocacao is AlocacaoAtual => Boolean(alocacao));

  if (encontradas.length < reais.length) return [motivoDaSaida(alteracao)];
  if (encontradas.some((alocacao) => isObraCancelada(alocacao.obra))) return ["obra-cancelada"];
  if (
    "alocacaoId" in alteracao &&
    encontradas.some((alocacao) => !MESMO_PERIODO(alocacao, alteracao.periodo))
  ) {
    return ["datas-alteradas"];
  }
  return [];
}

/** Prazo da revalidação: sem resposta a tempo, o Aplicar abre sem avisos (cada item é tratado na gravação); só a consulta que falha é erro. */
const PRAZO_REVALIDACAO_MS = 10_000;

function comPrazo(consulta: Promise<DivergenciaAplicacao[][]>): Promise<DivergenciaAplicacao[][]> {
  return Promise.race([
    consulta,
    new Promise<DivergenciaAplicacao[][]>((resolve) =>
      setTimeout(() => resolve([]), PRAZO_REVALIDACAO_MS),
    ),
  ]);
}

/**
 * Ao clicar em "Aplicar Simulação": reconsulta os dados atuais (só leitura, pelos mesmos
 * gateways do front) e compara com o ponto de partida de cada alteração. Devolve os
 * potenciais problemas, um por alteração afetada, com o desfecho que o Aplicar dará (RN020–RN023).
 * Consulta que falha rejeita: quem abre o Aplicar avisa o erro em vez de seguir sem revalidar.
 */
export async function revalidarCenario(cenario: CenarioSimulacao): Promise<DivergenciaAplicacao[]> {
  const consultar = consultas();
  const simulados = new Set(
    cenario.alteracoes
      .filter((alteracao) => alteracao.acao === "definir-equipe")
      .flatMap(alocacoesDaAlteracao),
  );

  const porAlteracao = await comPrazo(
    Promise.all(
      cenario.alteracoes.map(async (alteracao): Promise<DivergenciaAplicacao[]> => {
        const motivos = await motivosDe(alteracao, simulados, consultar);
        return motivos.slice(0, 1).map((motivo) => ({
          chave: chaveAlteracao(alteracao),
          motivo,
          ...DIVERGENCIA[motivo],
        }));
      }),
    ),
  );
  return porAlteracao.flat();
}
